import type { NetworkBusEvents } from './types.js'

type Listener<E extends keyof NetworkBusEvents> = (payload: NetworkBusEvents[E]) => void

// Use a Map keyed by event name to a Set of opaque listener functions. Cast
// at the boundary on/emit so each call site stays correctly-typed without
// fighting indexed-conditional types.
type OpaqueListener = (payload: unknown) => void
const listeners = new Map<keyof NetworkBusEvents, Set<OpaqueListener>>()

const getOrCreate = (event: keyof NetworkBusEvents): Set<OpaqueListener> => {
  const existing = listeners.get(event)
  if (existing) {
    return existing
  }
  const fresh = new Set<OpaqueListener>()
  listeners.set(event, fresh)
  return fresh
}

export const networkBus = {
  on<E extends keyof NetworkBusEvents>(event: E, listener: Listener<E>): () => void {
    const set = getOrCreate(event)
    const opaque = listener as OpaqueListener
    set.add(opaque)
    return () => {
      set.delete(opaque)
    }
  },
  emit<E extends keyof NetworkBusEvents>(event: E, payload: NetworkBusEvents[E]): void {
    const set = listeners.get(event)
    if (!set) {
      return
    }
    for (const fn of set) {
      ;(fn as Listener<E>)(payload)
    }
  },
}
